import React, { useCallback, useEffect, useMemo, useState } from "react";
import moment from "moment";
import Model from "@tripian/model";
import { PreLoading, SvgIcons } from "@tripian/react";
import { useNavigate, useSearchParams } from "react-router-dom";
import useCities from "../../hooks/useCities";
import useTourCategories from "../../hooks/useTourCategories";
import useTourSearchList, {
  TourSearchListFilters,
} from "../../hooks/useTourSearchList";
import useTranslate from "../../hooks/useTranslate";
import isTourApiEnabled from "../../utils/isTourApiEnabled";
import AppNav from "../../App/AppNav/AppNav";
import {
  LOCAL_EXPERIENCES,
  TOURS_AND_TICKETS,
} from "../../constants/ROUTER_PATH_TITLE";
import GygTourInfo from "../TourInfo/GygTourInfo/GygTourInfo";
import VistorTourInfo from "../TourInfo/VistorTourInfo/VistorTourInfo";
import ToristyTourInfo from "../TourInfo/ToristyTourInfo/ToristyTourInfo";
import VictoryProductInfo from "../TourInfo/VictoryProductInfo/VictoryProductInfo";
import LivnTourInfo from "../TourInfo/LivnTourInfo/LivnTourInfo";
import ToursAndTicketsSearchForm, {
  ToursAndTicketsDestination,
} from "./ToursAndTicketsSearchForm";
import TourFilters, { SORT_OPTIONS } from "./TourFilters";
import TourListCard from "./TourListCard";

const defaultArrival = () => moment().add(1, "days").format("YYYY-MM-DD");
const defaultDeparture = () => moment().add(7, "days").format("YYYY-MM-DD");

const emptyFilters: TourSearchListFilters = {
  categoryTags: [],
  providerIds: [],
};

const PROVIDER_NAMES: Partial<Record<number, string>> = {
  [Model.PROVIDER_ID.GYG]: "GetYourGuide",
  [Model.PROVIDER_ID.VIATOR]: "Viator",
  [Model.PROVIDER_ID.CIVITATIS]: "Civitatis",
  [Model.PROVIDER_ID.TORISTY]: "Toristy",
  [Model.PROVIDER_ID.BOOK_BARBADOS]: "Bookbarbados",
  [Model.PROVIDER_ID.REZDY]: "Rezdy",
  [Model.PROVIDER_ID.GOCITY]: "GoCity",
  [Model.PROVIDER_ID.VICTORY]: "Victory Live",
  [Model.PROVIDER_ID.LIVN]: "Livn",
  [Model.PROVIDER_ID.OCCASION_GENIUS]: "OccasionGenius",
};

/**
 * Detayı sayfa içinde overlay olarak açılabilen provider'lar — bu bileşenler trip
 * detayda da aynı şekilde modal olarak kullanılıyor. Listedeki ürün başka bir
 * provider'a aitse (BookBarbados, Rezdy, GoCity, Civitatis…) ürünün kendi url'i yeni
 * sekmede açılır; trip detaydaki handleTourClick de aynı ayrımı yapıyor.
 */
const OVERLAY_PROVIDER_IDS: Model.PROVIDER_ID[] = [
  Model.PROVIDER_ID.GYG,
  Model.PROVIDER_ID.VIATOR,
  Model.PROVIDER_ID.TORISTY,
  Model.PROVIDER_ID.VICTORY,
  Model.PROVIDER_ID.LIVN,
];

const optionalNumber = (value: string | null): number | undefined => {
  if (!value) return undefined;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? undefined : parsed;
};

const commaList = (value: string | null): string[] =>
  (value || "")
    .split(",")
    .map(item => item.trim())
    .filter(Boolean);

/**
 * Arama durumu (şehir, tarih, yolcu, filtreler, sıralama) URL'de tutuluyor.
 * Bileşen state'inde tutulduğunda tur detayına gidip geri dönmek sayfayı yeniden
 * mount ettiği için liste boşalıyordu; URL'de durunca geri navigasyon durumu
 * kendiliğinden geri yüklüyor, arama da paylaşılabilir/yer imine eklenebilir oluyor.
 */
const buildSearchParams = (
  profile: Model.TourAndTickets,
  filters: TourSearchListFilters,
  sortId: string
): URLSearchParams => {
  const params = new URLSearchParams();
  params.set("city_id", String(profile.cityId));
  if (profile.cityName) params.set("city_name", profile.cityName);
  if (profile.lat) params.set("lat", String(profile.lat));
  if (profile.lng) params.set("lng", String(profile.lng));
  params.set("start_date", profile.arrivalDatetime);
  params.set("end_date", profile.departureDatetime);
  params.set("adult", String(profile.adult || 1));
  if (profile.children) params.set("children", String(profile.children));

  if (filters.categoryTags.length > 0)
    params.set("categories", filters.categoryTags.join(","));
  if (filters.providerIds.length > 0)
    params.set("providers", filters.providerIds.join(","));
  if (filters.minPrice !== undefined)
    params.set("min_price", String(filters.minPrice));
  if (filters.maxPrice !== undefined)
    params.set("max_price", String(filters.maxPrice));
  if (filters.minRating !== undefined)
    params.set("min_rating", String(filters.minRating));
  if (filters.minDuration !== undefined)
    params.set("min_duration", String(filters.minDuration));
  if (filters.maxDuration !== undefined)
    params.set("max_duration", String(filters.maxDuration));
  if (sortId && sortId !== SORT_OPTIONS[0].id) params.set("sort", sortId);

  return params;
};

const ToursandTicketsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { cities, loadingCities } = useCities();
  const { t } = useTranslate();
  const navigate = useNavigate();

  const tourApiMode = isTourApiEnabled();

  /** URL'de şehir varsa aktif arama var demektir */
  const searchedProfile = useMemo<Model.TourAndTickets | null>(() => {
    const cityId = Number(searchParams.get("city_id")) || 0;
    if (!cityId) return null;

    return {
      cityId,
      cityName: searchParams.get("city_name") || "",
      lat: Number(searchParams.get("lat")) || 0,
      lng: Number(searchParams.get("lng")) || 0,
      arrivalDatetime: searchParams.get("start_date") || defaultArrival(),
      departureDatetime: searchParams.get("end_date") || defaultDeparture(),
      adult: Number(searchParams.get("adult")) || 1,
      children: optionalNumber(searchParams.get("children")),
    };
  }, [searchParams]);

  const filters = useMemo<TourSearchListFilters>(
    () => ({
      categoryTags: commaList(searchParams.get("categories")),
      providerIds: commaList(searchParams.get("providers")).map(Number),
      minPrice: optionalNumber(searchParams.get("min_price")),
      maxPrice: optionalNumber(searchParams.get("max_price")),
      minRating: optionalNumber(searchParams.get("min_rating")),
      minDuration: optionalNumber(searchParams.get("min_duration")),
      maxDuration: optionalNumber(searchParams.get("max_duration")),
    }),
    [searchParams]
  );

  const sortId = searchParams.get("sort") || SORT_OPTIONS[0].id;

  // Arama çubuğunun taslağı: URL'deki arama varsa onunla, yoksa varsayılanlarla açılır
  const [draftProfile, setDraftProfile] = useState<Model.TourAndTickets>(
    () =>
      searchedProfile ?? {
        cityId: window.tconfig.DEFAULT_DESTINATION_ID ?? 0,
        cityName: "",
        lat: 0,
        lng: 0,
        arrivalDatetime: defaultArrival(),
        departureDatetime: defaultDeparture(),
        adult: 1,
      }
  );

  const [showMobileFilters, setShowMobileFilters] = useState(false);

  const destinations: ToursAndTicketsDestination[] = useMemo(() => {
    if (!cities) return [];
    return cities.map(city => ({
      destinationId: city.id,
      destinationName: city.name,
      coordinate: city.coordinate,
      parentName:
        city.parentLocations.length === 0
          ? city.country.name
          : `${city.parentLocations.map(parent => parent.name).join(", ")}, ${city.country.name}`,
    }));
  }, [cities]);

  // Config'ten ya da URL'den gelen şehrin adı/koordinatı eksikse listeden tamamlanır
  const profile: Model.TourAndTickets = useMemo(() => {
    if (!draftProfile.cityId || (draftProfile.cityName && draftProfile.lat))
      return draftProfile;
    const selected = destinations.find(
      destination => destination.destinationId === draftProfile.cityId
    );
    if (!selected) return draftProfile;
    return {
      ...draftProfile,
      cityName: selected.destinationName,
      lat: selected.coordinate.lat,
      lng: selected.coordinate.lng,
    };
  }, [draftProfile, destinations]);

  const activeSort = useMemo(
    () => SORT_OPTIONS.find(option => option.id === sortId) || SORT_OPTIONS[0],
    [sortId]
  );

  const listFilters = useMemo<TourSearchListFilters>(
    () => ({
      ...filters,
      sortingBy: activeSort.sortingBy,
      sortingType: activeSort.sortingType,
    }),
    [filters, activeSort]
  );

  // Kategoriler yalnızca şehre göre çekiliyor, sağlayıcıya göre daraltılmıyor:
  // arama da sağlayıcı filtresi seçilmedikçe tüm sağlayıcıları kapsıyor, ayrıca
  // seçim yapıldıkça listenin kısalıp işaretli kutuların kaybolması kafa karıştırırdı.
  const { categories, loading: loadingCategories } = useTourCategories(
    searchedProfile?.cityId ? String(searchedProfile.cityId) : undefined,
    undefined
  );

  // `total` bilinçli olarak alınmıyor: sonuç sayısını göstermek için çeviri anahtarı
  // yok (bkz. resultsHeader). Anahtar eklenince hook'tan çekilebilir.
  const { products, loading, loadingMore, hasMore, error, loadMore } =
    useTourSearchList({
      cityId: searchedProfile?.cityId,
      lat: searchedProfile?.lat,
      lng: searchedProfile?.lng,
      date: searchedProfile?.arrivalDatetime,
      to: searchedProfile?.departureDatetime,
      adults: searchedProfile?.adult || 1,
      children: searchedProfile?.children,
      currency: "usd",
      lang: window.twindow?.langCode || "en",
      filters: listFilters,
      limit: 10,
    });

  // tourApi kapalıysa sayfa eskisi gibi sonuçları LocalExperiences'a devreder
  const navigateToLegacyResults = useCallback(() => {
    const params = new URLSearchParams({
      city_id: String(profile.cityId),
      city_name: profile.cityName,
      lat: String(profile.lat),
      lng: String(profile.lng),
      start_date: profile.arrivalDatetime,
      end_date: profile.departureDatetime,
      adult: String(profile.adult),
    });
    if (profile.children) params.set("children", String(profile.children));

    navigate(`${LOCAL_EXPERIENCES.PATH}?${params.toString()}`, {
      state: { from: TOURS_AND_TICKETS.PATH },
    });
  }, [navigate, profile]);

  /**
   * URL'i güncellemeler `replace` ile yapılıyor: her filtre değişikliği geçmişe
   * ayrı bir kayıt bırakırsa geri tuşu listede takılıp kalır. Tek kayıt kalınca
   * detaydan geri dönüş doğrudan güncel aramaya düşüyor.
   */
  const applySearch = (
    nextProfile: Model.TourAndTickets,
    nextFilters: TourSearchListFilters,
    nextSortId: string
  ) => {
    setSearchParams(buildSearchParams(nextProfile, nextFilters, nextSortId), {
      replace: true,
    });
  };

  const search = () => {
    if (!tourApiMode) {
      navigateToLegacyResults();
      return;
    }
    // Yeni aramada filtreler önceki şehrin kategorilerine ait olabilir, sıfırlanır
    applySearch(profile, emptyFilters, SORT_OPTIONS[0].id);
  };

  const changeFilters = (nextFilters: TourSearchListFilters) => {
    if (!searchedProfile) return;
    applySearch(searchedProfile, nextFilters, sortId);
  };

  const changeSort = (nextSortId: string) => {
    if (!searchedProfile) return;
    applySearch(searchedProfile, filters, nextSortId);
  };

  /**
   * Tur detayı ayrı bir rotaya gitmek yerine sayfa içinde overlay olarak açılıyor:
   * liste unmount olmadığı için geri dönüşte yeniden istek atılmıyor, kaydırma
   * konumu ve yüklenmiş sayfalar korunuyor.
   */
  const [selectedTour, setSelectedTour] = useState<{
    provider: Model.PROVIDER_ID;
    productId: string;
  } | null>(null);

  const openProduct = (product: Model.TourSearchProduct) => {
    const provider = product.providerId as Model.PROVIDER_ID;

    if (!OVERLAY_PROVIDER_IDS.includes(provider)) {
      if (product.url) window.open(product.url, "_blank");
      return;
    }

    // Overlay açılırken geçmişe bir kayıt bırakılıyor; böylece tarayıcı geri tuşu
    // sayfadan çıkmak yerine overlay'i kapatıyor.
    window.history.pushState({ tourOverlay: true }, "", window.location.href);
    setSelectedTour({ provider, productId: product.productId });
  };

  const closeSelectedTour = useCallback(() => {
    // Kapatma butonu da geri tuşuyla aynı yolu kullanıyor ki pushState ile eklenen
    // kayıt geçmişte artık kalmasın.
    if (window.history.state?.tourOverlay) {
      window.history.back();
      return;
    }
    setSelectedTour(null);
  }, []);

  useEffect(() => {
    if (!selectedTour) return undefined;

    const onPopState = () => setSelectedTour(null);
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [selectedTour]);

  // Sonsuz kaydırma: listenin sonundaki işaretçi görünür olunca sonraki sayfa
  const [sentinel, setSentinel] = useState<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!sentinel || !hasMore) return undefined;
    if (typeof IntersectionObserver === "undefined") return undefined;

    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) loadMore();
      },
      { rootMargin: "300px" }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [sentinel, hasMore, loadMore]);

  const filterPanel = (
    <TourFilters
      categories={categories}
      loadingCategories={loadingCategories}
      filters={filters}
      onFiltersChange={changeFilters}
      onClearAll={() => changeFilters(emptyFilters)}
      t={t}
    />
  );

  const resultsHeader = (
    // Sonuç sayısı gösterilmiyor: modelde "N sonuç" için bir çeviri anahtarı yok ve
    // çıplak bir sayı başlıkta belirsiz duruyor. Anahtar eklenirse `total` hazır.
    <div className="flex items-center justify-end gap-4 mb-4">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setShowMobileFilters(true)}
          className="lg:hidden flex items-center gap-2 px-4 py-2 rounded-full bg-gray-100 text-sm text-gray-700"
        >
          <SvgIcons.Filter2 />
          {t("trips.filters.title")}
        </button>
        <label className="flex items-center gap-2 text-sm text-gray-600">
          <span className="hidden sm:inline">{t("trips.filters.sort")}</span>
          <select
            value={sortId}
            onChange={event => changeSort(event.target.value)}
            className="px-3 py-2 text-sm bg-white border border-gray-300 rounded-full outline-none focus:border-primary-color"
          >
            {SORT_OPTIONS.map(option => (
              <option key={option.id} value={option.id}>
                {t(option.label)}
              </option>
            ))}
          </select>
        </label>
      </div>
    </div>
  );

  const results = () => {
    if (!searchedProfile) {
      return (
        <div className="py-20 text-center text-gray-500">
          {t("trips.toursAndTickets.destination.placeholder")}
        </div>
      );
    }

    if (loading) {
      return (
        <div className="py-20">
          <PreLoading />
        </div>
      );
    }

    if (error) {
      return (
        <div className="py-20 text-center text-gray-500">
          {t("trips.toursAndTickets.error.anErrorOccured")}
        </div>
      );
    }

    if (products.length === 0) {
      return (
        <div className="py-20 text-center text-gray-500">
          {t("trips.myTrips.localExperiences.toursEmpty")}
        </div>
      );
    }

    return (
      <>
        <div className="flex flex-col gap-4">
          {products.map(product => (
            <TourListCard
              key={product.id}
              product={product}
              providerName={PROVIDER_NAMES[product.providerId] || ""}
              onSelect={openProduct}
              t={t}
            />
          ))}
        </div>
        {hasMore && <div ref={setSentinel} className="h-px w-full" />}
        {loadingMore && (
          <div className="py-6 flex justify-center">
            <PreLoading />
          </div>
        )}
      </>
    );
  };

  return (
    <>
      <AppNav
        header={TOURS_AND_TICKETS.HEADER?.(t("trips.toursAndTickets.title"))}
      />
      <div className="h-full overflow-y-auto bg-background-color">
        <div className="container px-4 md:px-8 py-6">
          <ToursAndTicketsSearchForm
            destinations={destinations}
            loadingDestinations={loadingCities}
            profile={profile}
            onProfileChange={setDraftProfile}
            onSubmit={search}
            t={t}
          />

          {tourApiMode && (
            <div className="mt-8 flex gap-6 items-start">
              <aside className="hidden lg:block w-72 flex-shrink-0 sticky top-4">
                {filterPanel}
              </aside>
              <section className="flex-1 min-w-0">
                {resultsHeader}
                {results()}
              </section>
            </div>
          )}
        </div>
      </div>

      {/* Tur detayı — trip detaydaki modal kullanımının aynısı, tam ekran */}
      {selectedTour?.provider === Model.PROVIDER_ID.GYG && (
        <GygTourInfo
          tourId={Number(selectedTour.productId)}
          cityId={searchedProfile?.cityId || 0}
          cityName={searchedProfile?.cityName}
          startDate={searchedProfile?.arrivalDatetime}
          endDate={searchedProfile?.departureDatetime}
          adults={searchedProfile?.adult}
          children={searchedProfile?.children}
          isOpen
          onClose={closeSelectedTour}
          t={t}
        />
      )}
      {selectedTour?.provider === Model.PROVIDER_ID.VIATOR && (
        <VistorTourInfo
          tourId={selectedTour.productId}
          cityId={searchedProfile?.cityId || 0}
          cityName={searchedProfile?.cityName}
          lat={searchedProfile?.lat}
          lng={searchedProfile?.lng}
          startDate={searchedProfile?.arrivalDatetime}
          endDate={searchedProfile?.departureDatetime}
          adults={searchedProfile?.adult}
          children={searchedProfile?.children}
          /**
           * Yol + query string olarak veriliyor, çıplak TOURS_AND_TICKETS.PATH
           * olarak değil: VistorTourInfo (:1009) previousRoute'u tam olarak o yola
           * eşitse rezervasyon sonrası kullanıcıyı /local-experiences'a atıyor —
           * eski akıştan kalma bir kural. Query string'li hali o dala girmediği
           * gibi, aramayı da koruyarak bu sayfaya geri döndürüyor.
           */
          previousRoute={`${TOURS_AND_TICKETS.PATH}${window.location.search}`}
          isOpen
          onClose={closeSelectedTour}
        />
      )}
      {selectedTour?.provider === Model.PROVIDER_ID.TORISTY && (
        <ToristyTourInfo
          tourId={selectedTour.productId}
          isOpen
          onClose={closeSelectedTour}
        />
      )}
      {selectedTour?.provider === Model.PROVIDER_ID.VICTORY && (
        <VictoryProductInfo
          productId={selectedTour.productId}
          isOpen
          onClose={closeSelectedTour}
        />
      )}
      {selectedTour?.provider === Model.PROVIDER_ID.LIVN && (
        <LivnTourInfo
          tourId={Number(selectedTour.productId)}
          startDate={searchedProfile?.arrivalDatetime}
          endDate={searchedProfile?.departureDatetime}
          isOpen
          onClose={closeSelectedTour}
        />
      )}

      {/* Mobil filtre çekmecesi */}
      {showMobileFilters && (
        <div className="lg:hidden fixed inset-0 z-[1200] flex">
          <div
            className="absolute inset-0 bg-black bg-opacity-40"
            role="button"
            tabIndex={0}
            onKeyPress={() => {}}
            onClick={() => setShowMobileFilters(false)}
          />
          <div className="relative ms-auto w-80 max-w-[85%] h-full bg-background-color overflow-y-auto p-4">
            <div className="flex justify-end mb-2">
              <button
                type="button"
                onClick={() => setShowMobileFilters(false)}
                className="w-9 h-9 rounded-full bg-white border border-gray-200 flex items-center justify-center"
              >
                <SvgIcons.SearchClose width="16" height="16" />
              </button>
            </div>
            {filterPanel}
          </div>
        </div>
      )}
    </>
  );
};

export default ToursandTicketsPage;
